import React, { useMemo } from "react";
import { valueToString } from "helpers/Helpers";
import classNames from "classnames";

import BaseReportTable from "./BaseReportTable";

import "components/reportTable/reportTable.scss";
import "../pages/roundsByBookingCategory.scss";
import { formatReportCurrency } from "helpers/reportFormat";

interface IProps {
  data: Array<Record<string, any>>;
  columns: Array<Record<string, any>>;
  description: string;
  compileDate: string;
}

/** Nest flat leaf columns that share a header_group into subColumns for two-row headers. */
function nestGroupedColumns(columns: Array<Record<string, any>>): Array<Record<string, any>> {
  const nested: Array<Record<string, any>> = [];
  let currentGroupId: string | null = null;
  let currentGroupDisplay: string | null = null;
  let groupColumns: Array<Record<string, any>> = [];

  const flushGroup = () => {
    if (currentGroupId === null || currentGroupDisplay === null) {
      return;
    }

    nested.push({
      id: currentGroupId,
      display: currentGroupDisplay,
      locked: false,
      subColumns: groupColumns,
    });
    currentGroupId = null;
    currentGroupDisplay = null;
    groupColumns = [];
  };

  columns.forEach(column => {
    const headerGroup = column.header_group as string | undefined;
    const headerGroupId = (column.header_group_id as string | undefined) ?? headerGroup;

    if (!headerGroup || !headerGroupId) {
      flushGroup();
      nested.push(column);
      return;
    }

    if (currentGroupId !== null && currentGroupId !== headerGroupId) {
      flushGroup();
    }

    currentGroupId = headerGroupId;
    currentGroupDisplay = headerGroup;
    const leafColumn = { ...column };
    delete leafColumn.header_group;
    delete leafColumn.header_group_id;
    groupColumns.push(leafColumn);
  });

  flushGroup();

  return nested;
}

export default function RoundsByBookingCategoryTable(props: IProps) {
  const nestedColumns = useMemo(() => nestGroupedColumns(props.columns ?? []), [props.columns]);

  function formatDisplay(type: string, value: string) {
    let newValue = value !== null && value !== undefined ? value : "";

    if (!isNaN(Number(newValue)) && newValue !== "") {
      switch (type) {
        case "currency":
          newValue = formatReportCurrency(value);
          break;
        case "decimal":
          newValue = Number(value).toFixed(2);
          break;
        case "percentage":
          newValue = `${(Number(value) * 100).toFixed(2)}%`;
          break;
        default:
          break;
      }
    } else if (type === "string" && newValue !== "") {
      newValue = valueToString(value).replace(/(^\w|\s\w)/g, c => c.toUpperCase());
    }

    return newValue;
  }

  function cellData(columnId: string, cellValue: string, displayType: string) {
    if (columnId === "date") {
      return cellValue ?? "";
    }

    return formatDisplay(displayType, cellValue);
  }

  function cellOnClick() {
    return {};
  }

  function cellClassNames(cell: any, isRowLocked: boolean) {
    return classNames({
      "report-table-row-locked": isRowLocked,
    });
  }

  function footerData(columnId: string, footerRow?: Record<string, any>) {
    if (!footerRow) {
      return null;
    }

    if (columnId === "date") {
      return footerRow?.date as string;
    }

    return (footerRow[columnId] as string) ?? null;
  }

  return (
    <div className="rounds-by-booking-category-report">
      <BaseReportTable
        data={props.data}
        columns={nestedColumns}
        description={props.description}
        compileDate={props.compileDate}
        cellOnClick={cellOnClick}
        cellData={cellData}
        cellClassNames={cellClassNames}
        footerData={footerData}
        expanding={false}
      />
    </div>
  );
}
